import { useState, useCallback, useMemo, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { TextArea } from '../components/ui/TextArea';
import { Alert } from '../components/ui/Alert';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import type { ATSData, AgreementDetail, CalculationResult } from '../types';
import {
  fetchATSData,
  executeCalculation,
  validateCalculation,
  getActiveAgreementForHospital,
  type ATSResponse,
} from '../services/calculationService';

// Date-only fields that should strip the timestamp portion
const DATE_ONLY_FIELDS = new Set<keyof ATSData>(['doa', 'otDate', 'dod']);

// Format a value for display — strips time from date-only fields
function formatATSValue(key: keyof ATSData, val: unknown): string {
  if (val == null || val === '' || val === 'null' || val === 'undefined') return 'NA';
  if (DATE_ONLY_FIELDS.has(key) && typeof val === 'string' && val.includes('T')) {
    return val.split('T')[0];
  }
  return String(val);
}

// ATS fields organized by category sections
const ATS_SECTIONS: { title: string; fields: { key: keyof ATSData; label: string }[] }[] = [
  {
    title: 'Patient & Appointment',
    fields: [
      { key: 'apptId', label: 'Appointment ID' },
      { key: 'patientId', label: 'Patient ID' },
      { key: 'patientName', label: 'Patient Name' },
      { key: 'groupName', label: 'Group Name' },
      { key: 'appType', label: 'App Type' },
      { key: 'leadId', label: 'Lead ID' },
      { key: 'leadSource', label: 'Lead Source' },
      { key: 'inhouseTag', label: 'Inhouse Tag' },
      { key: 'createdBy', label: 'Created By' },
    ],
  },
  {
    title: 'Hospital & Doctor',
    fields: [
      { key: 'hospitalId', label: 'Hospital ID' },
      { key: 'hospitalName', label: 'Hospital Name' },
      { key: 'city', label: 'City' },
      { key: 'partnerHospitalName', label: 'Partner Hospital Name' },
      { key: 'noOfIpds', label: 'No of IPDs' },
      { key: 'doctorName', label: 'Doctor Name' },
      { key: 'doctorCode', label: 'Doctor Code' },
      { key: 'bdName', label: 'BD Name' },
      { key: 'bdName2', label: 'BD Name (2)' },
      { key: 'concernedTeam', label: 'Concerned Team' },
    ],
  },
  {
    title: 'Dates & Stay',
    fields: [
      { key: 'doa', label: 'DOA' },
      { key: 'otDate', label: 'OT Date' },
      { key: 'dod', label: 'DOD' },
      { key: 'ipdStatus', label: 'IPD Status' },
      { key: 'stayDays', label: 'Stay Days' },
      { key: 'currentStay', label: 'Current Stay' },
      { key: 'aging', label: 'Aging' },
    ],
  },
  {
    title: 'Surgery & Disease',
    fields: [
      { key: 'category', label: 'Category' },
      { key: 'subCategory', label: 'Sub-Category' },
      { key: 'disease', label: 'Disease' },
      { key: 'minorDisease', label: 'Minor Disease' },
      { key: 'opthalNonOpthal', label: 'Opthal/Non Opthal' },
      { key: 'surgeryCount', label: 'Surgery Count' },
      { key: 'otCount', label: 'OT Count' },
      { key: 'multipleSurgeryRemarks', label: 'Multiple Surgery Remarks' },
      { key: 'assistSubCategory', label: 'Assist Sub-Category' },
      { key: 'band', label: 'Band' },
      { key: 'band2', label: 'Band-2' },
    ],
  },
  {
    title: 'Payment & Insurance',
    fields: [
      { key: 'paymentMode', label: 'Payment Mode' },
      { key: 'tpaName', label: 'TPA Name' },
      { key: 'insuranceCompany', label: 'Insurance Company' },
      { key: 'approvalAmount', label: 'Approval Amount' },
      { key: 'insuranceSettlementAmount', label: 'Insurance Settlement Amount' },
      { key: 'settlementReceivedBy', label: 'Settlement Received By' },
      { key: 'extraCashReceived', label: 'Extra Cash Received' },
    ],
  },
  {
    title: 'Billing',
    fields: [
      { key: 'techAmount', label: 'Tech Amount' },
      { key: 'grossBillAmount', label: 'Gross Bill Amount' },
      { key: 'discount', label: 'Discount' },
      { key: 'netBillAmount', label: 'Net Bill Amount' },
    ],
  },
  {
    title: 'ATS',
    fields: [
      { key: 'projectedAts', label: 'Projected ATS' },
      { key: 'atsWithGst', label: 'ATS with GST' },
      { key: 'atsWithoutGst', label: 'ATS without GST' },
      { key: 'atsStatus', label: 'ATS Status' },
      { key: 'atsCollectionAuditStatus', label: 'ATS Collection Audit Status' },
      { key: 'atsForDrPayout', label: 'ATS For DR Payout' },
      { key: 'finalAtsForSharingOtherPayout', label: 'Final ATS for Sharing Other Payout' },
    ],
  },
  {
    title: 'Revenue & Collection',
    fields: [
      { key: 'revenueLossCalculated', label: 'Revenue Loss Calculated' },
      { key: 'revenueLossCopsDashboard', label: 'Revenue Loss COPS Dashboard' },
      { key: 'collectableFromPatient', label: 'Collectable from Patient' },
      { key: 'totalCollection', label: 'Total Collection' },
      { key: 'collectedAtGhv', label: 'Collected at GHV' },
      { key: 'collectedAtHospital', label: 'Collected at Hospital' },
      { key: 'excessAmount', label: 'Excess Amount' },
      { key: 'totalAmountReceivedByGhv', label: 'Total Amount Received by GHV' },
      { key: 'totalAmountReceivedByHospital', label: 'Total Amount Received by Hospital' },
    ],
  },
  {
    title: 'Lens & Implants',
    fields: [
      { key: 'lensName', label: 'Lens Name' },
      { key: 'vendorPriceIncludingTax', label: 'Vendor Price (Including Tax)' },
      { key: 'lensCostMrp', label: 'Lens Cost/MRP' },
      { key: 'ghvImplant', label: 'GHV Implant' },
      { key: 'ghvConsumable', label: 'GHV Consumable' },
      { key: 'ghvEquipment', label: 'GHV Equipment' },
      { key: 'hosImplantCount', label: 'Hos Implant (Count)' },
      { key: 'hosEquipmentCount', label: 'Hos Equipment (Count)' },
      { key: 'hosConsumableCount', label: 'Hos Consumable (Count)' },
      { key: 'pcImplantCount', label: 'PC Implant (Count)' },
      { key: 'pcEquipmentCount', label: 'PC Equipment (Count)' },
      { key: 'pcConsumableCount', label: 'PC Consumable (Count)' },
      { key: 'lensPendency', label: 'Lens Pendency' },
      { key: 'implantCostPendency', label: 'Implant Cost Pendency' },
      { key: 'equipmentCostPendency', label: 'Equipment Cost Pendency' },
      { key: 'consumableCostPendency', label: 'Consumable Cost Pendency' },
    ],
  },
];

const ERROR_CATEGORIES = [
  { value: 'HOSPITAL_SHARE_INCORRECT', label: 'Hospital Share Incorrect' },
  { value: 'AR_AP_INCORRECT', label: 'AR/AP Incorrect' },
  { value: 'PARAMETER_VALUE_INCORRECT', label: 'Parameter Value Incorrect' },
  { value: 'SYSTEM_VARIABLE_INCORRECT', label: 'System Variable Incorrect' },
];

type PageStep = 'entry' | 'input' | 'results' | 'validated';

export default function DataInputPage() {
  useAuth();

  const [apptId, setApptId] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [atsResponse, setAtsResponse] = useState<ATSResponse | null>(null);
  const [agreement, setAgreement] = useState<AgreementDetail | null>(null);

  // Input values auto-populated from ATS
  const [inputValues, setInputValues] = useState<Record<string, string>>({});

  const [isCalculating, setIsCalculating] = useState(false);
  const [calcResult, setCalcResult] = useState<CalculationResult | null>(null);
  const [calcError, setCalcError] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [showNotOkForm, setShowNotOkForm] = useState(false);
  const [errorCategory, setErrorCategory] = useState('');
  const [remarks, setRemarks] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveError, setSaveError] = useState('');

  const [step, setStep] = useState<PageStep>('entry');

  // Reset state on unmount so navigating back starts fresh
  useEffect(() => {
    return () => {
      setStep('entry');
    };
  }, []);

  const sortedInputParams = useMemo(() => {
    if (!agreement?.inputParameters) return [];
    return [...agreement.inputParameters]
      .filter((p) => p.parameterType === 'INPUT' && p.sourceType === 'MANUAL')
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }, [agreement]);

  // Initialize inputValues for MANUAL parameters (preserve existing values)
  useEffect(() => {
    if (!sortedInputParams.length) return;
    setInputValues((prev) => {
      const vals: Record<string, string> = { ...prev };
      for (const param of sortedInputParams) {
        if (!(param.parameterName in vals)) {
          vals[param.parameterName] = '';
        }
      }
      return vals;
    });
  }, [sortedInputParams]);

  // When user types a new appointment ID, reset to entry state
  useEffect(() => {
    if (step !== 'entry') {
      setStep('entry');
      setAtsResponse(null);
      setAgreement(null);
      setInputValues({});
      setCalcResult(null);
      setCalcError('');
      setFetchError('');
      setSaveSuccess('');
      setSaveError('');
      setShowNotOkForm(false);
      setErrorCategory('');
      setRemarks('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apptId]);

  const handleFetchData = useCallback(async () => {
    if (!apptId.trim()) return;
    setIsFetching(true);
    setFetchError('');
    setAtsResponse(null);
    setAgreement(null);
    setCalcResult(null);
    setInputValues({});
    setSaveSuccess('');
    setSaveError('');
    setShowNotOkForm(false);
    setErrorCategory('');
    setRemarks('');
    setStep('entry');

    try {
      const atsRes = await fetchATSData(apptId.trim());
      setAtsResponse(atsRes);

      const agr = await getActiveAgreementForHospital(
        atsRes.hospitalUuid,
        atsRes.atsData.doa || undefined
      );
      if (!agr) {
        setFetchError(`No active agreement found for hospital "${atsRes.hospitalName}".`);
        setIsFetching(false);
        return;
      }
      setAgreement(agr);
      setStep('input');
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to fetch appointment data.';
      setFetchError(msg);
    } finally {
      setIsFetching(false);
    }
  }, [apptId]);

  const handleCalculate = useCallback(async () => {
    if (!agreement || !atsResponse) return;
    setIsCalculating(true);
    setCalcError('');
    setCalcResult(null);
    setSaveSuccess('');
    setSaveError('');
    setShowNotOkForm(false);

    const typedValues: Record<string, unknown> = {};
    for (const param of sortedInputParams) {
      const raw = inputValues[param.parameterName] || '';
      if (param.dataType === 'NUMBER') {
        typedValues[param.parameterName] = raw ? Number(raw) : null;
      } else {
        typedValues[param.parameterName] = raw || null;
      }
    }

    try {
      const result = await executeCalculation(apptId.trim(), agreement.agreementId, typedValues);
      setCalcResult(result);
      setStep('results');
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Calculation failed.';
      setCalcError(msg);
    } finally {
      setIsCalculating(false);
    }
  }, [agreement, atsResponse, apptId, inputValues, sortedInputParams]);

  const handleValidate = useCallback(
    async (status: 'OK' | 'NOT_OK') => {
      if (!calcResult || !agreement || !atsResponse) return;
      if (status === 'NOT_OK' && (!errorCategory || !remarks.trim())) return;

      setIsSaving(true);
      setSaveError('');
      setSaveSuccess('');

      const typedValues: Record<string, unknown> = {};
      for (const param of sortedInputParams) {
        const raw = inputValues[param.parameterName] || '';
        if (param.dataType === 'NUMBER') {
          typedValues[param.parameterName] = raw ? Number(raw) : null;
        } else {
          typedValues[param.parameterName] = raw || null;
        }
      }

      try {
        await validateCalculation({
          apptId: apptId.trim(),
          hospitalUuid: atsResponse.hospitalUuid,
          agreementId: agreement.agreementId,
          agreementVersion: agreement.version,
          atsData: atsResponse.atsData,
          inputValues: typedValues,
          calculatedParameters: calcResult.calculatedParameters,
          hospitalShare: calcResult.hospitalShare,
          arAp: calcResult.arAp,
          validationStatus: status,
          errorCategory: status === 'NOT_OK' ? errorCategory : undefined,
          remarks: status === 'NOT_OK' ? remarks.trim() : undefined,
          executionTimeMs: calcResult.executionTimeMs,
        });
        setSaveSuccess(
          status === 'OK'
            ? 'Calculation validated and saved successfully.'
            : 'Calculation flagged as Not OK and saved.'
        );
        setStep('validated');
        setShowNotOkForm(false);
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || 'Failed to save validation.';
        setSaveError(msg);
      } finally {
        setIsSaving(false);
      }
    },
    [calcResult, agreement, atsResponse, apptId, inputValues, sortedInputParams, errorCategory, remarks]
  );

  const handleReset = useCallback(() => {
    setApptId('');
    setAtsResponse(null);
    setAgreement(null);
    setInputValues({});
    setCalcResult(null);
    setCalcError('');
    setFetchError('');
    setSaveSuccess('');
    setSaveError('');
    setShowNotOkForm(false);
    setErrorCategory('');
    setRemarks('');
    setStep('entry');
  }, []);

  const hasManualParams = sortedInputParams.length > 0;
  const showSplit = atsResponse && (step === 'input' || step === 'results');

  return (
    <div className="data-input-page data-input-compact">
      {/* Header row: title + appointment search inline */}
      <div className="di-top-bar">
        <h2>Data Input Panel</h2>
        <div className="di-top-bar-actions">
          <input
            className="di-appt-input"
            type="text"
            value={apptId}
            onChange={(e) => setApptId(e.target.value)}
            placeholder="Enter Appointment ID"
            disabled={isFetching}
            onKeyDown={(e) => { if (e.key === 'Enter') handleFetchData(); }}
          />
          <Button
            onClick={handleFetchData}
            isLoading={isFetching}
            disabled={!apptId.trim() || isFetching}
            size="sm"
          >
            Fetch Data
          </Button>
          {step !== 'entry' && (
            <Button variant="ghost" size="sm" onClick={handleReset}>
              New
            </Button>
          )}
        </div>
      </div>
      {fetchError && <div style={{ marginTop: '0.5rem' }}><Alert type="error">{fetchError}</Alert></div>}

      {/* Agreement info bar */}
      {agreement && (
        <div className="di-agreement-bar">
          <a
            href={`/agreements/${agreement.agreementId}/edit`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: '2px' }}
          >
            📋 Agreement #{agreement.agreementId} (v{agreement.version})
          </a>
          <span className="di-agreement-dates">
            {new Date(agreement.validFrom).toLocaleDateString()} → {new Date(agreement.validTill).toLocaleDateString()}
          </span>
        </div>
      )}

      {/* Side-by-side: System Variables (left) + Manual Inputs (right) */}
      {showSplit && (
        <div className="di-split-layout">
          {/* LEFT: ATS System Variables */}
          <div className="di-split-left">
            <div className="di-panel-header">
              <h3>📊 System Variables <span className="di-badge di-badge-readonly">Read-Only</span></h3>
            </div>
            <div className="di-panel-body">
              {ATS_SECTIONS.map((section) => (
                <details key={section.title} className="ats-section-group" open>
                  <summary className="ats-section-title">{section.title}</summary>
                  <div className="ats-data-grid">
                    {section.fields.map(({ key, label }) => {
                      const val = atsResponse!.atsData[key];
                      const display = formatATSValue(key, val);
                      return (
                        <div key={key} className="ats-field">
                          <span className="ats-field-label">{label}</span>
                          <span className={`ats-field-value${display === 'NA' ? ' ats-field-na' : ''}`}>{display}</span>
                        </div>
                      );
                    })}
                  </div>
                </details>
              ))}
            </div>
          </div>

          {/* RIGHT: Manual Input Parameters + Calculate */}
          <div className="di-split-right">
            <div className="di-panel-header">
              <h3>✏️ Input Parameters <span className="di-badge di-badge-manual">Operator Entry</span></h3>
            </div>
            <div className="di-panel-body di-input-panel-body">
              {hasManualParams ? (
                <div className="di-input-fields">
                  {sortedInputParams.map((param) => (
                    <div key={param.parameterId} className="form-field">
                      <label htmlFor={`input-${param.parameterId}`}>
                        {param.parameterName}
                        {param.validationRules?.required && <span className="required-mark"> *</span>}
                      </label>
                      {param.dataType === 'DROPDOWN' && param.dropdownValues ? (
                        <select
                          id={`input-${param.parameterId}`}
                          value={inputValues[param.parameterName] || ''}
                          onChange={(e) => setInputValues((prev) => ({ ...prev, [param.parameterName]: e.target.value }))}
                        >
                          <option value="">Select...</option>
                          {param.dropdownValues.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          id={`input-${param.parameterId}`}
                          type={param.dataType === 'NUMBER' ? 'number' : param.dataType === 'DATE' ? 'date' : 'text'}
                          value={inputValues[param.parameterName] || ''}
                          onChange={(e) => setInputValues((prev) => ({ ...prev, [param.parameterName]: e.target.value }))}
                          placeholder={`Enter ${param.parameterName}`}
                          min={param.dataType === 'NUMBER' && param.validationRules?.min !== undefined ? param.validationRules.min : undefined}
                          max={param.dataType === 'NUMBER' && param.validationRules?.max !== undefined ? param.validationRules.max : undefined}
                        />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="di-no-params">
                  <p>No manual input parameters required for this agreement. All values come from ATS system data.</p>
                </div>
              )}
              <div className="di-calculate-action">
                <Button
                  onClick={handleCalculate}
                  isLoading={isCalculating}
                  disabled={isCalculating}
                  size="lg"
                >
                  Calculate
                </Button>
                {isCalculating && <LoadingSpinner size="sm" message="Calculating..." />}
                {calcError && <Alert type="error">{calcError}</Alert>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Calculation Results — full width */}
      {calcResult && step !== 'entry' && (
        <section className="data-input-section" aria-label="Calculation results">
          <h3>Calculation Results</h3>
          {!calcResult.success && calcResult.errors && (
            <Alert type="warning">Calculation completed with errors. Review the details below.</Alert>
          )}
          <div className="calc-results-summary">
            <div className="calc-result-card calc-result-primary">
              <span className="calc-result-label">Hospital Share</span>
              <span className="calc-result-value">
                {typeof calcResult.hospitalShare === 'number'
                  ? calcResult.hospitalShare.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  : '—'}
              </span>
            </div>
            <div className="calc-result-card calc-result-primary">
              <span className="calc-result-label">AR/AP</span>
              <span className="calc-result-value">
                {typeof calcResult.arAp === 'number'
                  ? calcResult.arAp.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  : '—'}
              </span>
            </div>
            <div className="calc-result-card">
              <span className="calc-result-label">Execution Time</span>
              <span className="calc-result-value">{calcResult.executionTimeMs} ms</span>
            </div>
          </div>

          {Object.keys(calcResult.calculatedParameters).length > 0 && (
            <div className="calc-params-table-wrap">
              <h4>Calculated Parameters</h4>
              <table className="data-table">
                <thead><tr><th>Parameter</th><th>Value</th></tr></thead>
                <tbody>
                  {Object.entries(calcResult.calculatedParameters).map(([name, value]) => (
                    <tr key={name}><td>{name}</td><td>{value != null ? String(value) : '—'}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {calcResult.errors && calcResult.errors.length > 0 && (
            <div className="calc-errors">
              <h4>Errors</h4>
              {calcResult.errors.map((err, i) => (
                <Alert key={i} type="error">
                  {err.parameter && <strong>{err.parameter}: </strong>}
                  {err.error}
                </Alert>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Validation Workflow */}
      {calcResult && step === 'results' && (
        <section className="data-input-section" aria-label="Validation">
          <h3>Validation</h3>
          <div className="validation-actions">
            <Button variant="primary" size="lg" onClick={() => handleValidate('OK')} isLoading={isSaving} disabled={isSaving}>OK</Button>
            <Button variant="danger" size="lg" onClick={() => setShowNotOkForm(true)} disabled={isSaving || showNotOkForm}>Not OK</Button>
          </div>

          {showNotOkForm && (
            <div className="not-ok-form">
              <Select
                label="Error Category"
                options={ERROR_CATEGORIES}
                placeholder="Select error category..."
                value={errorCategory}
                onChange={(e) => setErrorCategory(e.target.value)}
              />
              <TextArea
                label="Remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Describe the issue..."
                rows={3}
                error={showNotOkForm && !remarks.trim() ? 'Remarks are required for Not OK validation' : undefined}
              />
              <div className="not-ok-form-actions">
                <Button variant="danger" onClick={() => handleValidate('NOT_OK')} isLoading={isSaving} disabled={!errorCategory || !remarks.trim() || isSaving}>Submit Not OK</Button>
                <Button variant="ghost" onClick={() => { setShowNotOkForm(false); setErrorCategory(''); setRemarks(''); }} disabled={isSaving}>Cancel</Button>
              </div>
            </div>
          )}
          {saveError && <Alert type="error">{saveError}</Alert>}
        </section>
      )}

      {saveSuccess && (
        <section className="data-input-section">
          <Alert type="success">{saveSuccess}</Alert>
        </section>
      )}
    </div>
  );
}

